import { useEffect, useState, useRef } from 'react';
import { shallow } from 'zustand/shallow';

import { VIEWPORT_OPTIONS, PREVIEW_SOURCE, IFRAME_ELEMENT_ID, PREVIEW_NO_LAYOUT_SOURCE, ERRORS } from './lib';

import { useDynamicEntityStoreContext, useDynamicEntityCMSDataContext } from './contexts';
import { useIframeContentSync } from './hooks/useIframeContentSync';
import { useZoom } from './hooks/useZoom';

import ViewportOptions from './_builder_editor/ViewportOptions';
import PageOptionsBar from './_builder_editor/PageOptionsBar';
import GlobalStateModal from './_builder_editor/GlobalStateModal';

export default function BuilderEditor({ isEdit }) {
  const { isModule, isTemplate } = useDynamicEntityCMSDataContext();

  const assignedCategory = useDynamicEntityStoreContext((state) => state.data.assigned_category);
  const isAssignedToJobs = useDynamicEntityStoreContext((state) => state.data.is_assigned_to_jobs);
  const componentBeingEditedIndex = useDynamicEntityStoreContext((state) => state.componentBeingEditedIndex);
  const activePane = useDynamicEntityStoreContext((state) => state.activePane);
  const isError = useDynamicEntityStoreContext((state) => state.isError);
  const errorKeys = useDynamicEntityStoreContext((state) => state.errorKeys, shallow);
  const update = useDynamicEntityStoreContext((state) => state.update);
  const content = useDynamicEntityStoreContext((state) => state.data.content, shallow);
  const viewport = useDynamicEntityStoreContext((state) => state.viewport);

  const isLayoutMode = isTemplate && !assignedCategory && !isAssignedToJobs;
  const INITIAL_PREVIEW_SOURCE = isLayoutMode ? PREVIEW_NO_LAYOUT_SOURCE : PREVIEW_SOURCE;

  const iFrameRef = useRef(null);
  const [source, setSource] = useState(INITIAL_PREVIEW_SOURCE);
  const [fullSource, setFullSource] = useState(`/${INITIAL_PREVIEW_SOURCE}?t=${new Date().getTime()}`);
  const [isLoading, setIsLoading] = useState(false);
  const [isLayoutAvailable, setIsLayoutAvailable] = useState(false);

  const { zoom, handleZoomIn, handleZoomOut } = useZoom();

  const handleOnParentReceive = (eventData) => {
    const eventType = eventData.type;
    if (!eventType) return;

    switch (eventType) {
      case 'EDITABLE_ELEMENT_CLICKED':
        const { id, propName } = eventType;
        const componentBeingEditedIndex = content.findIndex((item) => item.id === id);

        update({ componentBeingEditedIndex, elementBeingEditedName: propName, activePane: 3 });
    }
  };

  const { handleIframeLoad } = useIframeContentSync({
    iFrameRef,
    content,
    isSourceAvailable: isLayoutMode ? isLayoutAvailable : true,
    onParentReceive: handleOnParentReceive,
    onIframeLoad: () => setIsLoading(false),
  });

  const handleRefresh = () => {
    setIsLoading(true);
    setFullSource(`/${source}?t=${new Date().getTime()}`);
  };

  const handlePreviewModeChange = (newSource) => {
    setSource(newSource);
    setFullSource(`/${newSource}?t=${new Date().getTime()}`);
  };

  useEffect(() => {
    if (componentBeingEditedIndex === undefined) return;

    const currentContentBeingEditedId = content[componentBeingEditedIndex].id;

    const message = JSON.stringify({ id: currentContentBeingEditedId });

    iFrameRef?.current?.contentWindow?.postMessage({ type: 'FOCUS_CONTENT', message }, '*');
  }, [componentBeingEditedIndex]);

  useEffect(() => {
    if (activePane === 3 || activePane === 6 || (isModule && activePane === 2)) {
      return;
    }

    update({ componentBeingEditedIndex: undefined, elementBeingEditedName: undefined });

    iFrameRef?.current?.contentWindow?.postMessage({ type: 'CLEAR_FOCUS_CONTENT' }, '*');
  }, [activePane, isModule, update]);

  useEffect(() => {
    fetch(`/${PREVIEW_NO_LAYOUT_SOURCE}`, { method: 'HEAD' })
      .then((res) => {
        if (res.ok) {
          setIsLayoutAvailable(true);
        } else {
          setIsLayoutAvailable(false);
        }
      })
      .catch(() => {
        setIsLayoutAvailable(false);
      });
  }, []);

  return (
    <div className="dp-editor-right">
      <div className="top-input">
        <div className="d-flex gap-2">
          <button className="btn btn-outline top-button" type="button" onClick={handleRefresh}>
            <div className={`${isLoading ? 'rotate' : ''}`}>
              <i className="fe fe-refresh-cw" />
            </div>
          </button>
          <div className="vr text-muted py-2" />
          <div className="d-flex gap-3 align-items-center justify-content-center">
            <button className="btn btn-outline top-button" type="button" onClick={handleZoomOut}>
              <i className="fe fe-zoom-out" />
            </button>
            <span>{zoom.label}</span>
            <button className="btn btn-outline top-button" type="button" onClick={handleZoomIn}>
              <i className="fe fe-zoom-in" />
            </button>
          </div>
        </div>
        <ViewportOptions />
        <PageOptionsBar {...{ isEdit, handleRefresh }} />
      </div>
      <div className="iframe-preview">
        {isLoading && (
          <div className="spinner-overlay">
            <div className="spinner-border text-dark" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        )}
        <iframe
          key={source}
          onLoad={handleIframeLoad}
          id={IFRAME_ELEMENT_ID}
          ref={iFrameRef}
          src={fullSource}
          className={`${VIEWPORT_OPTIONS[viewport].className}`}
          style={{ zoom: zoom.value }}
        />
      </div>
      <div className="bottom-input">
        <div className="preview-selector">
          <span className="title">Preview</span>
          <div className="inner-container">
            <button
              type="button"
              className={`preview-button ${source === PREVIEW_SOURCE ? 'active' : ''}`}
              onClick={() => handlePreviewModeChange(PREVIEW_SOURCE)}
            >
              Normal
            </button>
            <button
              type="button"
              className={`preview-button ${source === PREVIEW_NO_LAYOUT_SOURCE ? 'active' : ''}`}
              onClick={() => handlePreviewModeChange(PREVIEW_NO_LAYOUT_SOURCE)}
              disabled={!isLayoutAvailable}
            >
              No Layout
            </button>
          </div>
          {isLayoutMode && !isLayoutAvailable && <span className="text-danger">Please use 'Normal' for now as 'No Layout' hasn't been added yet.</span>}
        </div>
        <div className="error-indicator">
          <div className="dropup-center dropup">
            <button className="btn error-button" type="button" data-bs-toggle="dropdown" aria-expanded="false">
              {isError ? (
                <span className="text-danger d-flex align-items-center gap-3">
                  See Errors
                  <i className="fe fe-alert-triangle" />
                </span>
              ) : (
                <span className="text-success d-flex align-items-center gap-3">
                  No Errors
                  <i className="fe fe-check-circle" />
                </span>
              )}
            </button>
            <ul className="dropdown-menu">
              {isError ? (
                errorKeys.map((err, index) => (
                  <li key={index} className="dropdown-item text-danger">
                    {ERRORS.find((error) => error.key === err).message}
                  </li>
                ))
              ) : (
                <li className="dropdown-item text-success">All good! No errors found.</li>
              )}
            </ul>
          </div>
        </div>

        <div className="kitchen-sink">
          <div className="dropup-center dropup">
            <button className="btn" type="button" data-bs-toggle="dropdown" aria-expanded="false">
              <i className="fe fe-more-horizontal" />
            </button>
            <ul className="dropdown-menu">
              <button className="dropdown-item" data-bs-toggle="modal" data-bs-target={`#globalStateJsonModal`}>
                Global State
              </button>
            </ul>
          </div>
        </div>
        <GlobalStateModal />
      </div>
    </div>
  );
}
